import type {
	DeployStatusEvent,
	DiffChangeEvent,
	LockChangeEvent,
} from "@repo/game-admin"
import { useQueryClient } from "@tanstack/react-query"
import * as React from "react"
import { toast } from "sonner"
import { useOrpcWs } from "@/integrations/orpc/ws-react"
import { formatGameName } from "@/utils/game.helpers"

const ENTITY_CHANGE_MESSAGES = {
	created: { verb: "created", icon: toast.info },
	updated: { verb: "updated", icon: toast.info },
	deleted: { verb: "deleted", icon: toast.warning },
} as const

const ENTITY_TYPE_PLURALS: Record<string, string> = {
	product: "products",
	expansion: "expansions",
	expansionGroup: "expansion groups",
	game: "games",
}

/** Quiet time after the last entity change before its grouped toast shows. */
const ENTITY_CHANGE_TOAST_DELAY_MS = 400

interface EntityChangeGroup {
	gameSlug: string
	entityType: string
	changeType: keyof typeof ENTITY_CHANGE_MESSAGES
	/** Distinct entities changed during the burst. */
	entitySlugs: Set<string>
	timer: ReturnType<typeof setTimeout>
}

/**
 * Collects manual entity changes and shows one toast per game, entity type
 * and change type once that group's burst goes quiet. A bulk save (e.g. the
 * AI matcher saving a whole expansion) emits one change per product, and a
 * product can change more than once within the burst.
 */
export function createEntityChangeToaster() {
	const groups = new Map<string, EntityChangeGroup>()

	const flush = (key: string) => {
		const group = groups.get(key)
		if (!group) return
		groups.delete(key)

		const config = ENTITY_CHANGE_MESSAGES[group.changeType]
		const game = formatGameName(group.gameSlug)
		const [first] = group.entitySlugs
		if (group.entitySlugs.size === 1) {
			const type =
				group.entityType.charAt(0).toUpperCase() + group.entityType.slice(1)
			config.icon(`${type} ${config.verb}`, {
				description: `${first} in ${game}`,
			})
			return
		}
		const plural =
			ENTITY_TYPE_PLURALS[group.entityType] ?? `${group.entityType}s`
		config.icon(`${group.entitySlugs.size} ${plural} ${config.verb}`, {
			description: `in ${game}`,
		})
	}

	return {
		add(change: {
			gameSlug: string
			entitySlug: string
			entityType: string
			changeType: keyof typeof ENTITY_CHANGE_MESSAGES
		}) {
			const key = `${change.gameSlug}|${change.entityType}|${change.changeType}`
			const existing = groups.get(key)
			if (existing) clearTimeout(existing.timer)
			const timer = setTimeout(() => flush(key), ENTITY_CHANGE_TOAST_DELAY_MS)
			if (existing) {
				existing.entitySlugs.add(change.entitySlug)
				existing.timer = timer
				return
			}
			groups.set(key, {
				gameSlug: change.gameSlug,
				entityType: change.entityType,
				changeType: change.changeType,
				entitySlugs: new Set([change.entitySlug]),
				timer,
			})
		},
		dispose() {
			for (const group of groups.values()) clearTimeout(group.timer)
			groups.clear()
		},
	}
}

const DIFF_STATUS_MESSAGES = {
	pending: {
		title: "New Diff Available",
		icon: toast.success,
		description: (change: DiffChangeEvent) =>
			`${change.totalChanges} changes for ${formatGameName(change.gameSlug)}`,
	},
	deployed: {
		title: "Diff Deployed",
		icon: toast.success,
		description: (change: DiffChangeEvent) =>
			`Successfully deployed changes for ${formatGameName(change.gameSlug)}`,
	},
	partially_deployed: {
		title: "Partial Deployment",
		icon: toast.info,
		description: (change: DiffChangeEvent) =>
			`Partially deployed changes for ${formatGameName(change.gameSlug)}`,
	},
} as const

const getLockMessage = (
	change: Pick<LockChangeEvent, "locked" | "gameSlug" | "acquiredBy">,
) => {
	if (change.locked) {
		return {
			title: "Resource Locked",
			icon: toast.warning,
			description: `${formatGameName(change.gameSlug || "Global")} locked by ${change.acquiredBy || "system"}`,
		}
	}
	return {
		title: "Resource Unlocked",
		icon: toast.success,
		description: `${formatGameName(change.gameSlug || "Global")} is now available`,
	}
}

const DEPLOY_STATUS_HANDLERS = {
	started: (status: DeployStatusEvent) => {
		toast.loading("Deployment Started...", {
			id: `deploy-${status.diffId}`,
			description: `Starting deployment for ${formatGameName(status.gameSlug)}`,
		})
	},
	completed: (status: DeployStatusEvent) => {
		toast.success("Deployment Complete", {
			id: `deploy-${status.diffId}`,
			description: `Successfully deployed ${status.entitiesDeployed} changes`,
		})
	},
	failed: (status: DeployStatusEvent) => {
		toast.error("Deployment Error", {
			id: `deploy-${status.diffId}`,
			description: status.error || "An error occurred during deployment",
		})
	},
} as const

/**
 * Hook to subscribe to real-time notifications and display toast
 * notifications, errors and handle query invalidations.
 *
 * Uses oRPC WebSocket subscriptions with async iterators for real-time events.
 * Waits for auth to be fully ready before starting subscriptions.
 */
export function useRealtimeNotifications() {
	const queryClient = useQueryClient()
	const { client, isAuthReady } = useOrpcWs()

	React.useEffect(() => {
		if (!isAuthReady || !client) {
			return
		}

		const controller = new AbortController()
		const entityChangeToaster = createEntityChangeToaster()

		const startSubscriptions = async () => {
			if (controller.signal.aborted) return
			subscribeEntityChanges()
			subscribeDiffChanges()
			subscribeDeployStatus()
			subscribeLockChanges()
		}

		// Entity change subscription
		const subscribeEntityChanges = async () => {
			try {
				const eventIterator = await client.manual.subscribeEntityChanges(
					{},
					{ signal: controller.signal },
				)

				for await (const change of eventIterator) {
					if (change.source !== "manual") continue
					if (!(change.changeType in ENTITY_CHANGE_MESSAGES)) continue
					entityChangeToaster.add({
						gameSlug: change.gameSlug,
						entitySlug: change.entitySlug,
						entityType: change.entityType || "entity",
						changeType:
							change.changeType as keyof typeof ENTITY_CHANGE_MESSAGES,
					})
				}
			} catch (error) {
				if (error instanceof Error && error.name === "AbortError") {
					return
				}
				console.error("Entity change subscription error:", error)
			}
		}

		// Diff change subscription
		const subscribeDiffChanges = async () => {
			try {
				const eventIterator = await client.diff.subscribeDiffChanges(
					{},
					{ signal: controller.signal },
				)

				for await (const change of eventIterator) {
					queryClient.invalidateQueries({
						queryKey: ["gameAdmin", "diff", "getAllActiveDiffsWithDetails"],
					})

					if (
						change.status === "deployed" ||
						change.status === "partially_deployed"
					) {
						continue
					}

					const config =
						DIFF_STATUS_MESSAGES[
							change.status as keyof typeof DIFF_STATUS_MESSAGES
						]
					if (config) {
						config.icon(config.title, {
							description: config.description(change),
						})
					}
				}
			} catch (error) {
				if (error instanceof Error && error.name === "AbortError") {
					return
				}
				console.error("Diff change subscription error:", error)
			}
		}

		// Deploy status subscription
		const subscribeDeployStatus = async () => {
			try {
				const eventIterator = await client.diff.subscribeDeployStatus(
					{},
					{ signal: controller.signal },
				)

				for await (const status of eventIterator) {
					const handler =
						DEPLOY_STATUS_HANDLERS[
							status.status as keyof typeof DEPLOY_STATUS_HANDLERS
						]
					if (handler) handler(status)
				}
			} catch (error) {
				if (error instanceof Error && error.name === "AbortError") {
					return
				}
				console.error("Deploy status subscription error:", error)
			}
		}

		// Lock change subscription
		const subscribeLockChanges = async () => {
			try {
				const eventIterator = await client.lock.subscribeLockChanges(
					{},
					{ signal: controller.signal },
				)

				for await (const change of eventIterator) {
					queryClient.invalidateQueries({
						queryKey: ["gameAdmin", "lock", "status"],
					})

					queryClient.invalidateQueries({
						queryKey: ["gameAdmin", "lock", "canAcquire"],
					})

					const message = getLockMessage({
						locked: change.locked,
						gameSlug: change.gameSlug,
						acquiredBy: change.acquiredBy,
					})

					message.icon(message.title, {
						description: message.description,
						duration: 3000,
					})
				}
			} catch (error) {
				if (error instanceof Error && error.name === "AbortError") {
					return
				}
				console.error("Lock change subscription error:", error)
			}
		}

		// Start all subscriptions
		startSubscriptions()

		return () => {
			controller.abort()
			entityChangeToaster.dispose()
		}
	}, [client, queryClient, isAuthReady])
}
